// @ts-check
// Seletor de variáveis das automações (contrato 08, seção 3.15), como o do Twenty: ao lado de cada campo de texto de um
// passo, um botão abre a lista do que dá para usar ali, por nome amigável: o registro do gatilho, o contato, o corpo do
// webhook ou o horário do agendamento, a instituição e a saída de cada passo anterior (o que roda antes dele e não
// está no outro ramo de um se/senão). Escolher insere a variável `{{...}}` no texto.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { Dropdown, DropdownMenuHeader, DropdownMenuItems, DropdownMenuSearchInput, DropdownMenuSectionLabel, MenuItem } from '../../ui/dropdown.js';
import { Icon } from '../../ui/icon.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { flattenActions, previousStepIndexes } from '../../../../shared/automations/automation-steps.js';
import { ACTION_ICONS, isRecordlessTrigger, isWatchableField, pickFields, recordKeyOf } from './automation-labels.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../data/automations.js').AutomationAction} AutomationAction
 * @typedef {{ token: string, path: string, label: string }} VariableItem
 * @typedef {{ key: string, label: string, icon: string, items: VariableItem[] }} VariableGroup
 * @typedef {{ objectMetadata: ObjectMetadata | undefined, triggerType: string, triggerSettings: Record<string, any>, actions: AutomationAction[] }} VariableScope
 */

const SKIPPED_FIELD_TYPES = new Set(['RAW_JSON', 'FILES', 'RICH_TEXT', 'TS_VECTOR', 'POSITION']);

/**
 * Variáveis dos campos de um objeto sob um prefixo (`record`, `contact`, `steps.<id>.record`).
 * @param {ObjectMetadata | undefined} objectMetadata @param {string} prefix @param {string} [labelPrefix]
 * @returns {VariableItem[]}
 */
export function fieldVariables(objectMetadata, prefix, labelPrefix = '') {
  return pickFields(objectMetadata, (field) => isWatchableField(field) && !SKIPPED_FIELD_TYPES.has(field.type)).flatMap((field) => {
    const label = `${labelPrefix}${translateMetadataLabel(field.label)}`;
    const key = field.type === 'RELATION' ? field.name : recordKeyOf(field);
    const path = `${prefix}.${key}`;
    const items = [{ token: `{{${path}}}`, path, label }];
    if (field.type === 'FULL_NAME') items.unshift({ token: `{{${path}.firstName}}`, path: `${path}.firstName`, label: `${label} (${t('automations.variables.firstName')})` });
    if (field.type === 'RELATION') items.push({ token: `{{${prefix}.${field.name}Id}}`, path: `${prefix}.${field.name}Id`, label: `${label} (id)` });
    return items;
  });
}

/** Chaves de um JSON de exemplo, até dois níveis (corpo esperado do webhook, resposta esperada). @param {string | null | undefined} example @param {string} prefix */
function exampleVariables(example, prefix) {
  if (!example) return [];
  let parsed;
  try {
    parsed = JSON.parse(example);
  } catch {
    return [];
  }
  /** @type {VariableItem[]} */
  const items = [];
  /** @param {any} value @param {string} path @param {number} depth */
  const visit = (value, path, depth) => {
    if (!value || typeof value !== 'object' || Array.isArray(value) || depth > 2) return;
    for (const [key, child] of Object.entries(value)) {
      const childPath = `${path}.${key}`;
      items.push({ token: `{{${childPath}}}`, path: childPath, label: childPath.split('.').slice(2).join(' › ') });
      visit(child, childPath, depth + 1);
    }
  };
  visit(parsed, prefix, 1);
  return items;
}

/** Número de cada passo na árvore: 1, 2, 2 › Sim › 1... @param {AutomationAction[]} actions */
export function stepNumbers(actions) {
  /** @type {Map<string, string>} */
  const numbers = new Map();
  /** @param {AutomationAction[]} list @param {string} prefix */
  const visit = (list, prefix) => list.forEach((action, index) => {
    const number = `${prefix}${index + 1}`;
    if (action.id) numbers.set(action.id, number);
    if (action.type === 'IF_ELSE') {
      visit(action.branches?.yes ?? [], `${number} › ${t('automations.ifElse.yes')} › `);
      visit(action.branches?.no ?? [], `${number} › ${t('automations.ifElse.no')} › `);
    }
  });
  visit(actions, '');
  return numbers;
}

/** Nome amigável de um passo: número, tipo e objeto. @param {AutomationAction} action @param {string} number */
export function stepTitle(action, number) {
  const target = action.objectNameSingular ? objectsByNameSingular.value.get(action.objectNameSingular) : undefined;
  const objectLabel = target ? ` (${translateMetadataLabel(target.labelSingular)})` : '';
  return `${number}. ${action.name || t(`automations.actionTypes.${action.type}`)}${objectLabel}`;
}

/**
 * O que cada passo deixa para os seguintes.
 * @param {AutomationAction} action @returns {VariableItem[]}
 */
function stepOutputVariables(action) {
  const prefix = `steps.${action.id}`;
  /** @param {string} path @param {string} label */
  const item = (path, label) => ({ token: `{{${prefix}${path ? `.${path}` : ''}}}`, path: `${prefix}${path ? `.${path}` : ''}`, label });
  const target = action.objectNameSingular ? objectsByNameSingular.value.get(action.objectNameSingular) : undefined;
  switch (action.type) {
    case 'CREATE_RECORD':
    case 'UPDATE_RECORD':
      return [item('id', t('automations.variables.recordId')), item('label', t('automations.variables.recordLabel')), ...fieldVariables(target, `${prefix}.record`)];
    case 'FIND_RECORDS':
      return [
        item('count', t('automations.variables.count')), item('totalCount', t('automations.variables.totalCount')), item('labelList', t('automations.variables.labelList')),
        item('first.id', t('automations.variables.firstId')), item('first.label', t('automations.variables.firstLabel')),
        ...fieldVariables(target, `${prefix}.first.record`, `${t('automations.variables.first')} › `),
      ];
    case 'HTTP_REQUEST':
      return [item('status', t('automations.variables.httpStatus')), item('body', t('automations.variables.httpBody')), ...exampleVariables(action.expectedResponse, prefix)
        .map((variable) => ({ ...variable, path: variable.path.replace(prefix, `${prefix}.body`), token: variable.token.replace(prefix, `${prefix}.body`) }))];
    case 'CREATE_CALENDAR_EVENT':
      return [item('eventId', t('automations.variables.eventId')), item('title', t('automations.variables.eventTitle')), item('startsAt', t('automations.variables.eventStartsAt'))];
    case 'CREATE_TASK':
      return [item('taskId', t('automations.variables.taskId'))];
    case 'IF_ELSE':
      return [item('branch', t('automations.variables.branch'))];
    case 'SEND_EMAIL':
      return [item('messageId', t('automations.variables.messageId'))];
    default:
      return [];
  }
}

/**
 * Grupos de variáveis disponíveis para um passo (ou para as condições da automação, sem `actionId`).
 * @param {VariableScope} scope @param {string | null} actionId
 * @returns {VariableGroup[]}
 */
export function buildVariableGroups(scope, actionId) {
  const { objectMetadata, triggerType, triggerSettings, actions } = scope;
  /** @type {VariableGroup[]} */
  const groups = [];
  if (triggerType === 'WEBHOOK') {
    groups.push({
      key: 'trigger', label: t('automations.variables.webhookGroup'), icon: 'IconWebhook',
      items: [{ token: '{{trigger.body}}', path: 'trigger.body', label: t('automations.variables.wholeBody') }, ...exampleVariables(triggerSettings?.expectedBody, 'trigger.body'),
        { token: '{{trigger.receivedAt}}', path: 'trigger.receivedAt', label: t('automations.variables.receivedAt') }],
    });
  }
  if (triggerType === 'SCHEDULED') {
    groups.push({ key: 'trigger', label: t('automations.variables.scheduleGroup'), icon: 'IconClock', items: [{ token: '{{trigger.scheduledAt}}', path: 'trigger.scheduledAt', label: t('automations.variables.scheduledAt') }] });
  }
  if (!isRecordlessTrigger(triggerType, triggerSettings)) {
    groups.push({ key: 'record', label: objectMetadata ? translateMetadataLabel(objectMetadata.labelSingular) : t('automations.variables.recordGroup'), icon: 'IconTargetArrow',
      items: [{ token: '{{record.id}}', path: 'record.id', label: t('automations.variables.recordId') }, ...fieldVariables(objectMetadata, 'record')] });
    const hasContact = objectMetadata?.nameSingular === 'person' || objectMetadata?.fields.some((field) => field.type === 'RELATION' && field.relation?.targetObjectNameSingular === 'person');
    if (hasContact) groups.push({ key: 'contact', label: t('automations.variables.contactGroup'), icon: 'IconUser', items: fieldVariables(objectsByNameSingular.value.get('person'), 'contact') });
  }
  groups.push({
    key: 'institution', label: t('automations.variables.otherGroup'), icon: 'IconBuilding',
    items: [{ token: '{{institution.name}}', path: 'institution.name', label: t('automations.form.variableInstitution') },
      ...(isRecordlessTrigger(triggerType, triggerSettings) ? [] : [{ token: '{{recordUrl}}', path: 'recordUrl', label: t('automations.form.variableRecordUrl') }])],
  });
  if (actionId) {
    const flat = flattenActions(/** @type {any[]} */ (actions));
    const index = flat.findIndex((step) => step.action.id === actionId);
    const numbers = stepNumbers(actions);
    if (index > 0) {
      for (const position of previousStepIndexes(flat, index)) {
        const action = /** @type {AutomationAction} */ (flat[position].action);
        const items = stepOutputVariables(action);
        if (items.length > 0) groups.push({ key: `step-${action.id}`, label: stepTitle(action, numbers.get(action.id ?? '') ?? String(position + 1)), icon: ACTION_ICONS[/** @type {keyof typeof ACTION_ICONS} */ (action.type)] ?? 'IconSettingsAutomation', items });
      }
    }
  }
  return groups.filter((group) => group.items.length > 0);
}

/** Rótulo de uma variável já escrita, para mostrar nas condições. @param {VariableGroup[]} groups @param {string} path */
export function variableLabel(groups, path) {
  for (const group of groups) {
    const item = group.items.find((candidate) => candidate.path === path);
    if (item) return `${group.label} › ${item.label}`;
  }
  return path;
}

/**
 * Botão do seletor: primeiro a origem (gatilho, passos anteriores), depois o campo; a busca procura em tudo.
 * @param {{ groups: VariableGroup[], disabled?: boolean, onSelect: (item: VariableItem) => void, ariaLabel?: string }} props
 */
export function VariablePicker({ groups, disabled = false, onSelect, ariaLabel }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [groupKey, setGroupKey] = useState(/** @type {string | null} */ (null));
  const [search, setSearch] = useState('');
  const close = () => {
    setIsOpen(false);
    setGroupKey(null);
    setSearch('');
  };
  const choose = (/** @type {VariableItem} */ item) => {
    onSelect(item);
    close();
  };
  const query = search.trim().toLocaleLowerCase('pt-BR');
  const group = groups.find((candidate) => candidate.key === groupKey) ?? null;
  const matches = query
    ? groups.flatMap((candidate) => candidate.items.filter((item) => `${candidate.label} ${item.label}`.toLocaleLowerCase('pt-BR').includes(query)).map((item) => ({ group: candidate, item })))
    : [];
  return html`<span class="automation-variable-picker">
    <button type="button" ref=${anchorRef} class="automation-variable-button" disabled=${disabled} aria-label=${ariaLabel ?? t('automations.variables.insert')}
      title=${t('automations.variables.insert')} aria-expanded=${isOpen ? 'true' : 'false'} data-testid="variable-picker"
      onClick=${() => (isOpen ? close() : setIsOpen(true))}>
      <${Icon} name="IconBraces" size=${14} />
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${close} placement="bottom-end" width=${300} maxHeight=${360}>
      ${group
        ? html`<${DropdownMenuHeader} title=${group.label} onBack=${() => setGroupKey(null)} />`
        : html`<${DropdownMenuSearchInput} value=${search} onChange=${setSearch} placeholder=${t('automations.variables.search')} />`}
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${groups.length === 0 ? html`<div class="automation-muted automation-variable-empty">${t('automations.variables.empty')}</div>` : null}
        ${group
          ? group.items.map((item) => html`<${MenuItem} key=${item.path} text=${item.label} onClick=${() => choose(item)} />`)
          : query
            ? matches.map(({ group: owner, item }) => html`<${MenuItem} key=${`${owner.key}-${item.path}`} text=${item.label} contextualText=${owner.label} onClick=${() => choose(item)} />`)
            : html`<${DropdownMenuSectionLabel} label=${t('automations.variables.chooseSource')} />
              ${groups.map((candidate) => html`<${MenuItem} key=${candidate.key} icon=${candidate.icon} text=${candidate.label} hasSubMenu=${true} onClick=${() => setGroupKey(candidate.key)} />`)}`}
      <//>
    <//>
  </span>`;
}

/**
 * Campo de texto (ou área) com o seletor de variáveis ao lado; a variável entra na posição do cursor.
 * @param {{ label: string, value: string | null | undefined, onChange: (value: string) => void, groups: VariableGroup[], disabled?: boolean,
 *   multiline?: boolean, placeholder?: string, hint?: string, type?: string, testId?: string }} props
 */
export function VariableTextField({ label, value, onChange, groups, disabled = false, multiline = false, placeholder = '', hint, type = 'text', testId }) {
  const inputRef = useRef(/** @type {HTMLInputElement | HTMLTextAreaElement | null} */ (null));
  const insert = (/** @type {VariableItem} */ item) => {
    const current = value ?? '';
    const element = inputRef.current;
    const start = element && typeof element.selectionStart === 'number' && document.activeElement !== null ? element.selectionStart : current.length;
    const end = element && typeof element.selectionEnd === 'number' ? element.selectionEnd : start;
    onChange(`${current.slice(0, start ?? current.length)}${item.token}${current.slice(end ?? current.length)}`);
  };
  return html`<div class="form-field">
    <span class="form-field-label">${label}</span>
    <div class="automation-variable-input" data-multiline=${multiline ? 'true' : undefined}>
      ${multiline
        ? html`<textarea ref=${inputRef} class="text-input automation-textarea" rows="4" value=${value ?? ''} disabled=${disabled} placeholder=${placeholder} aria-label=${label}
            data-testid=${testId} onInput=${(event) => onChange(event.currentTarget.value)}></textarea>`
        : html`<input ref=${inputRef} class="text-input" type=${type} value=${value ?? ''} disabled=${disabled} placeholder=${placeholder} aria-label=${label}
            data-testid=${testId} onInput=${(event) => onChange(event.currentTarget.value)} />`}
      ${disabled || groups.length === 0 ? null : html`<${VariablePicker} groups=${groups} onSelect=${insert} ariaLabel=${t('automations.variables.insertInto', { field: label })} />`}
    </div>
    ${hint ? html`<span class="input-hint">${hint}</span>` : null}
  </div>`;
}
